import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import MainHeader from "../components/MainHeader";
import { TrendingUp, Ticket, DollarSign, CreditCard } from "lucide-react";

export default function AdminSales() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => { document.body.classList.remove("theme-mini"); }, []);
  useEffect(() => {
    if (user && user.role === "admin") api.get("/admin/sales/summary").then(({ data }) => setData(data));
  }, [user]);

  if (user === null) return null;
  if (!user || user.role !== "admin") return <Navigate to="/" replace />;
  if (!data) return null;

  return (
    <div data-testid="admin-sales-page" className="min-h-screen bg-[#F4F2EC]">
      <MainHeader />
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-12">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gs-ember">Projection booth</p>
            <h1 className="font-outfit font-semibold text-4xl text-gs-ink mt-2">Sales Dashboard</h1>
          </div>
          <div className="flex gap-3 font-mono text-[10px] uppercase tracking-widest">
            <Link to="/admin" className="text-gs-dust hover:text-gs-ink">Films</Link>
            <span className="text-gs-ember">Sales</span>
            <Link to="/admin/filmmakers" className="text-gs-dust hover:text-gs-ink">Filmmakers</Link>
          </div>
        </div>

        <div className="grid md:grid-cols-4 gap-4">
          <Stat icon={DollarSign} label="Gross revenue" value={`$${data.total_gross.toFixed(2)}`} />
          <Stat icon={Ticket} label="Tickets" value={data.total_tickets} />
          <Stat icon={TrendingUp} label="Buys" value={data.sales} />
          <Stat icon={CreditCard} label="Rentals" value={data.rentals} />
        </div>

        <div className="grid md:grid-cols-2 gap-6 mt-8">
          <div className="bg-white border border-black/5 rounded-xl p-6 shadow-sm">
            <p className="font-mono text-[10px] uppercase tracking-widest text-gs-dust mb-4">By payment provider</p>
            <div className="space-y-3">
              {Object.entries(data.by_provider).map(([k, v]) => (
                <div key={k} data-testid={`provider-${k}`} className="flex items-center justify-between">
                  <span className="font-outfit capitalize text-gs-ink">{k}</span>
                  <span className="font-mono text-sm text-gs-ink">{v.count} · ${v.gross.toFixed(2)}</span>
                </div>
              ))}
              {Object.keys(data.by_provider).length === 0 && (
                <p className="font-serif italic text-gs-ink/60 text-sm">No transactions yet.</p>
              )}
            </div>
          </div>

          <div className="bg-white border border-black/5 rounded-xl p-6 shadow-sm">
            <p className="font-mono text-[10px] uppercase tracking-widest text-gs-dust mb-4">Top films</p>
            <div className="space-y-3">
              {data.by_film.slice(0, 5).map((f, i) => (
                <div key={f.slug} data-testid={`top-film-${i}`} className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-outfit text-gs-ink truncate">{f.title}</p>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-gs-dust">{f.tickets} tickets</p>
                  </div>
                  <span className="font-display text-2xl text-gs-ember whitespace-nowrap">${f.gross.toFixed(2)}</span>
                </div>
              ))}
              {data.by_film.length === 0 && <p className="font-serif italic text-gs-ink/60 text-sm">No films sold yet.</p>}
            </div>
          </div>
        </div>

        <div className="bg-white border border-black/5 rounded-xl p-6 shadow-sm mt-6">
          <p className="font-mono text-[10px] uppercase tracking-widest text-gs-dust mb-4">Recent purchases</p>
          {data.recent.length === 0 ? (
            <p className="font-serif italic text-gs-ink/60 text-sm">No purchases yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-gs-dust border-b border-gs-line">
                    <th className="py-2">When</th>
                    <th>Film</th>
                    <th>User</th>
                    <th>Mode</th>
                    <th>Provider</th>
                    <th className="text-right">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gs-line">
                  {data.recent.map((p, i) => (
                    <tr key={p.id} data-testid={`recent-row-${i}`} className="text-gs-ink">
                      <td className="py-3 font-mono text-xs text-gs-dust">{new Date(p.created_at).toLocaleString()}</td>
                      <td className="font-outfit">{p.title}</td>
                      <td className="font-mono text-xs">{p.user_email}</td>
                      <td className="font-mono text-xs uppercase">{p.mode}</td>
                      <td className="font-mono text-xs">{p.payment_provider || "mock"}</td>
                      <td className="text-right font-display text-lg text-gs-ember">${(p.amount || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-black/5 rounded-xl p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <p className="font-mono text-[10px] uppercase tracking-widest text-gs-dust">{label}</p>
        <Icon size={16} className="text-gs-ember" />
      </div>
      <p className="font-display text-4xl text-gs-ink mt-3 tracking-wide">{value}</p>
    </div>
  );
}
